import { getApiBaseUrl } from "../../utils/ssoProvider";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IoWarningOutline } from "react-icons/io5";
import PopupModal from "../PopupModal";
import axiosInstance from "../../services/axiosInstance";
import { closeTemplateConflictsListModal } from "../../redux/templateConflictsListModalSlice";
import { openTemplateConflictModal } from "../../redux/templateConflictModalSlice";

const API_URL = getApiBaseUrl();

// Renders on every authenticated screen (mounted from layouts/ContentLayout.jsx), opened
// from the "Resolve now" button on GlobalTemplateConflictBanner.jsx. Lists every currently
// unresolved conflict pair with the two real template titles involved; picking "Resolve now"
// on a row hands that specific pair off to GlobalTemplateConflictModal for the actual fix.
//
// The list endpoint (GET /store/draft/template-conflicts) only returns template ids, not
// titles - so this also fetches the store's templates once to look titles/descriptions up by id.
const GlobalTemplateConflictsListModal = () => {
  const dispatch = useDispatch();
  const isOpen = useSelector(state => state.templateConflictsListModal.isOpen);
  const [conflictPairs, setConflictPairs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setIsLoading(true);

    Promise.all([
      axiosInstance.get(`${API_URL}/v1/store/draft/template-conflicts`, {
        params: { status_filter: "unresolved", page_size: 100 },
      }),
      axiosInstance.get(`${API_URL}/v1/store/draft/templates`, {
        params: { page_size: 100 },
      }),
    ])
      .then(([conflictsResponse, templatesResponse]) => {
        if (cancelled) return;
        const templateLookup = new Map((templatesResponse.data?.items ?? []).map(t => [t.id, t]));

        const pairs = (conflictsResponse.data?.items ?? []).map(conflict => {
          const templateA = templateLookup.get(conflict.template_a_id);
          const templateB = templateLookup.get(conflict.template_b_id);
          return {
            id: conflict.id,
            overlapPercentage: conflict.overlap_percentage,
            templateA: {
              id: conflict.template_a_id,
              name: templateA?.name || "Untitled template",
              description: templateA?.description || "",
            },
            templateB: {
              id: conflict.template_b_id,
              name: templateB?.name || "Untitled template",
              description: templateB?.description || "",
            },
          };
        });
        setConflictPairs(pairs);
      })
      .catch(error => {
        console.error("Failed to load the template conflicts list:", error);
        setConflictPairs([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleClose = () => {
    dispatch(closeTemplateConflictsListModal());
  };

  const handleResolveRow = pair => {
    // Hand off to the single-conflict resolution modal - closing this one first rather
    // than stacking both, so there's only ever one popup on screen at a time.
    dispatch(closeTemplateConflictsListModal());
    dispatch(
      openTemplateConflictModal({
        conflictId: pair.id,
        overlapPercent: pair.overlapPercentage,
        templateA: pair.templateA,
        templateB: pair.templateB,
      })
    );
  };

  return (
    <PopupModal closeModal={handleClose} className="bg-white w-[560px] max-w-[92vw] max-h-[80vh] overflow-y-auto">
      <div className="flex items-start gap-3 mb-4">
        <span className="flex-shrink-0 w-10 h-10 rounded-full bg-[#FEF3C7] flex items-center justify-center">
          <IoWarningOutline className="w-5 h-5 text-[#D97706]" />
        </span>
        <div>
          <h3 className="text-[16px] font-bold text-[#101828] font-dmsans">Conflicting templates</h3>
          <p className="text-[13px] text-gray-500 mt-1">
            These template pairs currently overlap. Resolve a pair to pick which one the AI should use.
          </p>
        </div>
      </div>

      {isLoading ? (
        <p className="text-[13px] text-gray-500 py-4 text-center">Loading conflicts...</p>
      ) : conflictPairs.length === 0 ? (
        <p className="text-[13px] text-gray-500 py-4 text-center">No unresolved conflicts remaining.</p>
      ) : (
        <div className="flex flex-col gap-2">
          {conflictPairs.map(pair => (
            <div
              key={pair.id}
              className="flex items-center justify-between gap-3 border border-gray-200 rounded-lg p-3 bg-[#F9FAFB]"
            >
              <p className="text-[13px] text-[#101828] min-w-0">
                <span className="font-semibold">"{pair.templateA.name}"</span> and{" "}
                <span className="font-semibold">"{pair.templateB.name}"</span> are having a conflict.
              </p>
              <button
                type="button"
                onClick={() => handleResolveRow(pair)}
                className="flex-shrink-0 px-3 py-1.5 rounded-md bg-[#2B2E3A] text-[12px] font-medium text-[#96F3C3] whitespace-nowrap"
              >
                Resolve now
              </button>
            </div>
          ))}
        </div>
      )}
    </PopupModal>
  );
};

export default GlobalTemplateConflictsListModal;
